import { Injector, inject, effect, WritableSignal, untracked, EffectCleanupRegisterFn } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DEFAULT_PAGINATION } from 'app/core/const/pagination.const';
import { IObjectTypeParse } from 'app/core/helpers/util';
import QueryString from 'qs';
import { Subscription, debounceTime, distinctUntilChanged, filter, map } from 'rxjs';

export function eventCurrentURL() {
    const router = inject(Router);
    return toSignal(router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).pipe(map((ev) => ev.url)), {
        initialValue: router.url,
    });
}

export function registerQueryParams(queryParams: any, fnc: () => void, options?: { injector: Injector }): Subscription {
    const { injector } = options;

    const activatedRoute = injector?.get(ActivatedRoute) ?? inject(ActivatedRoute);
    // const router = injector?.get(Router) ?? inject(Router);

    return activatedRoute.queryParams.pipe(distinctUntilChanged(), debounceTime(50)).subscribe((data) => {
        // if (Object.keys(data).length === 0) {
        //     router
        //         .navigate([], {
        //             relativeTo: activatedRoute,
        //             queryParams,
        //             queryParamsHandling: 'merge',
        //         })
        //         .then();
        //     return;
        // }

        fnc();
    });
}

export function parseQueryParamsByUrl<T>(url: string, objectTypeParse: IObjectTypeParse<keyof T>, defaultData: T) {
    const tempDefaultData = JSON.parse(JSON.stringify(defaultData));

    if (!url) {
        return tempDefaultData;
    }

    const urls = url.split('?');

    if (urls.length === 1) {
        return tempDefaultData;
    }

    let finalQueryParams = {} as T;

    let queryParams = QueryString.parse(urls[0]);

    if (url.length > 1) {
        queryParams = QueryString.parse(urls[1]);
    }

    Object.entries(objectTypeParse).forEach(([key, value]: any) => {
        const finalKey = key.replaceAll('[]', '');
        const tempValue = queryParams[finalKey] ?? null;
        let finalValue: number | string | boolean | any[] | undefined;

        const isEmpty = tempValue === '' || tempValue === null || tempValue === undefined;

        const parseValue = {
            boolean: (finalValue = tempValue === 'true'),
            number: !isEmpty ? Number(tempValue) : null,
            string: !(tempValue === null || tempValue === undefined) ? String(tempValue) : null,
            array: !isEmpty ? (Array.isArray(tempValue) ? tempValue : [tempValue]) : null,
            numberArray: !isEmpty ? (Array.isArray(tempValue) ? tempValue.map((tempValueMap) => Number(tempValueMap)) : [Number(tempValue)]) : null,
            stringArray: !isEmpty ? (Array.isArray(tempValue) ? tempValue.map((tempValueMap) => String(tempValueMap)) : [String(tempValue)]) : null,
        };

        finalValue = parseValue[value];

        if (key === 'limit' && Number(finalValue) > 100)  {
            finalValue = DEFAULT_PAGINATION;
        }

        finalQueryParams = {
            ...finalQueryParams,
            [key]: finalValue,
        };
    });

    return finalQueryParams;
}

export function getLastPathUrl(url: string): string {
    const newUrl = url.split('?')[0];

    const paths = newUrl.split('/');

    if (paths.length === 0) {
        return paths[0];
    }

    return paths[paths.length - 1];
}

export function effectQueryParams<TSource, K extends keyof TSource>(
    sourceSignal: () => TSource,
    properties: K[] | 'all',
    target: ITarget<TSource, K>,
    options: { injector: Injector },
): void {
    const { targetSignal, fnc } = target;
    const { injector } = options;

    let prevValue: Pick<TSource, K> | null = null;

    effect(
        (onCleanup) => {
            const sourceValue = sourceSignal();

            const derivedValue =
                properties === 'all'
                    ? sourceValue
                    : properties?.reduce(
                        (acc, prop) => {
                            acc[prop] = sourceValue[prop];
                            return acc;
                        },
                        {} as Pick<TSource, K>,
                    );

            if (JSON.stringify(derivedValue) === JSON.stringify(prevValue)) {
                return;
            }

            prevValue = derivedValue;

            untracked(() => {
                targetSignal?.set(sourceValue);
                fnc?.(sourceValue, onCleanup);
            });
        },
        { injector },
    );
}

export type ITarget<TSource, K extends keyof TSource> = {
    targetSignal?: WritableSignal<Pick<TSource, K>>;
    fnc?: (parameter: TSource, onCleanup: EffectCleanupRegisterFn) => void;
} & ({ targetSignal: WritableSignal<Pick<TSource, K>> } | { fnc: (parameter: TSource, onCleanup: EffectCleanupRegisterFn) => void });
